import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter, Youtube, MapPin, Phone, Mail, ExternalLink, Navigation } from "lucide-react";
import { Logo } from "./Logo";
import { BIZ, SOCIAL_LINKS } from "../lib/constants";

const TikTokIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.7a8.16 8.16 0 0 0 4.77 1.52V6.78a4.85 4.85 0 0 1-1.84-.09Z" />
    </svg>
);

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, twitter: Twitter, youtube: Youtube };

// Footer is locked to dark surface in BOTH themes for brand consistency.
const FOOTER_BG = "#0A0A0A";
const FOOTER_TEXT = "#FFFFFF";
const FOOTER_MUTED = "rgba(255,255,255,0.62)";
const FOOTER_GOLD = "#F5B027";
const FOOTER_BORDER = "rgba(255,255,255,0.10)";

const ColHeading = ({ children }) => (
    <h4 style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: FOOTER_GOLD,
        marginBottom: 22,
    }}>{children}</h4>
);

const FootLink = ({ to, external, children, testid }) => {
    const style = {
        color: FOOTER_TEXT,
        opacity: 0.85,
        fontSize: 14,
        lineHeight: 1.7,
        textDecoration: "none",
        transition: "color 0.2s ease, opacity 0.2s ease",
    };
    const cls = "footer-link";
    if (external) {
        return (
            <a href={to} target="_blank" rel="noopener noreferrer" style={style} className={cls} data-testid={testid}>
                {children}
            </a>
        );
    }
    return <Link to={to} style={style} className={cls} data-testid={testid}>{children}</Link>;
};

export const Footer = () => {
    const directionsHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(BIZ.address.full)}`;
    return (
        <footer
            data-testid="footer"
            style={{ background: FOOTER_BG, color: FOOTER_TEXT, marginTop: 80, position: "relative" }}
        >
            <style>{`
                .footer-link:hover { color: ${FOOTER_GOLD} !important; opacity: 1 !important; }
                .footer-social { width: 36px; height: 36px; border-radius: 999px; border: 1px solid #2a2a2a; display: inline-flex; align-items: center; justify-content: center; color: ${FOOTER_TEXT}; transition: all 0.2s ease; }
                .footer-social:hover { border-color: ${FOOTER_GOLD}; color: ${FOOTER_GOLD}; transform: translateY(-2px); }
                .footer-cta-btn-primary { display: inline-flex; align-items: center; gap: 8px; padding: 14px 22px; border-radius: 4px; background: #E74C3C; color: #fff; font-weight: 500; text-decoration: none; font-size: 14px; transition: background 0.2s ease, transform 0.2s ease; }
                .footer-cta-btn-primary:hover { background: #d94434; transform: translateY(-2px); }
                .footer-cta-btn-gold { display: inline-flex; align-items: center; gap: 8px; padding: 13px 21px; border-radius: 4px; background: transparent; color: ${FOOTER_GOLD}; border: 1px solid ${FOOTER_GOLD}; font-weight: 500; text-decoration: none; font-size: 14px; transition: all 0.2s ease; }
                .footer-cta-btn-gold:hover { background: ${FOOTER_GOLD}; color: #0A0A0A; }
                .footer-cta-btn-outline { display: inline-flex; align-items: center; gap: 8px; padding: 13px 21px; border-radius: 4px; background: transparent; color: #fff; border: 1px solid rgba(255,255,255,0.4); font-weight: 500; text-decoration: none; font-size: 14px; transition: all 0.2s ease; }
                .footer-cta-btn-outline:hover { border-color: #fff; background: rgba(255,255,255,0.06); }
                @media (max-width: 768px) {
                    .footer-cta-strip { text-align: center; }
                    .footer-cta-buttons { flex-direction: column; align-items: stretch; }
                    .footer-cta-buttons > * { justify-content: center; }
                    .footer-cols { text-align: center; }
                    .footer-cols .footer-social-row { justify-content: center; }
                    .footer-legal { text-align: center; }
                }
            `}</style>

            {/* CTA STRIP */}
            <section
                className="footer-cta-strip"
                aria-label="Ready to stock up"
                data-testid="footer-cta-strip"
                style={{
                    background: "linear-gradient(135deg, #1a1410 0%, #0A0A0A 100%)",
                    borderTop: `1px solid rgba(245,176,39,0.25)`,
                    borderBottom: `1px solid rgba(245,176,39,0.25)`,
                    padding: "56px 0",
                }}
            >
                <div className="ls-container">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                        <div style={{ maxWidth: 560 }}>
                            <h2 className="font-display" style={{ fontSize: "clamp(28px, 3.6vw, 42px)", fontWeight: 500, lineHeight: 1.1, letterSpacing: "-0.02em", color: "#fff" }}>
                                Ready to <em style={{ fontStyle: "italic", color: FOOTER_GOLD, fontWeight: 400 }}>stock up?</em>
                            </h2>
                            <p style={{ marginTop: 12, color: "rgba(255,255,255,0.78)", fontSize: 15, lineHeight: 1.6 }}>
                                Shop online, call ahead for a reserve, or just stop by 309 S 2nd Street.
                            </p>
                        </div>
                        <div className="footer-cta-buttons flex flex-wrap gap-3" data-testid="footer-cta-buttons">
                            <a href={BIZ.shopUrl} target="_blank" rel="noopener noreferrer" className="footer-cta-btn-primary" data-testid="footer-cta-shop">
                                Shop Online <ExternalLink size={14} />
                            </a>
                            <a href={`tel:${BIZ.phoneTel}`} className="footer-cta-btn-gold" data-testid="footer-cta-call">
                                <Phone size={14} /> Call {BIZ.phone}
                            </a>
                            <a href={directionsHref} target="_blank" rel="noopener noreferrer" className="footer-cta-btn-outline" data-testid="footer-cta-directions">
                                <Navigation size={14} /> Get Directions
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* MAIN COLUMNS */}
            <div className="footer-cols ls-container" style={{ padding: "72px 0 48px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 48 }}>
                    {/* BRAND */}
                    <div>
                        <Logo size={80} />
                        <p style={{ marginTop: 22, color: FOOTER_MUTED, fontSize: 14, lineHeight: 1.7, maxWidth: 280 }}>
                            Clinton, Missouri's premier spirits destination. Bourbon, wine, craft beer & more — curated for Henry County and Truman Lake.
                        </p>
                        <div className="footer-social-row flex gap-3 mt-7" data-testid="footer-socials">
                            {SOCIAL_LINKS.map(({ key, label, url }) => {
                                const Icon = SOCIAL_ICONS[key];
                                return (
                                    <a
                                        key={key}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`Liquor Studio on ${label}`}
                                        className="footer-social"
                                        data-testid={`footer-social-${key}`}
                                    >
                                        {Icon ? <Icon size={16} /> : <TikTokIcon size={16} />}
                                    </a>
                                );
                            })}
                        </div>
                    </div>

                    {/* EXPLORE */}
                    <div>
                        <ColHeading>Explore</ColHeading>
                        <ul className="flex flex-col gap-3" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                            <li><FootLink to="/spirits" testid="foot-spirits">Spirits Catalog</FootLink></li>
                            <li><FootLink to="/blog" testid="foot-blog">Studio Journal</FootLink></li>
                            <li><FootLink to="/recipes" testid="foot-recipes">Cocktail Recipes</FootLink></li>
                            <li><FootLink to="/faq" testid="foot-faq">FAQ</FootLink></li>
                            <li><FootLink to="/local/clinton-mo" testid="foot-local">Local Clinton Guide</FootLink></li>
                            <li><FootLink to="/reviews" testid="foot-reviews">Customer Reviews</FootLink></li>
                            <li><FootLink to="/about" testid="foot-about">Our Story</FootLink></li>
                        </ul>
                    </div>

                    {/* SHOP */}
                    <div>
                        <ColHeading>Shop &amp; Services</ColHeading>
                        <ul className="flex flex-col gap-3" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                            <li>
                                <FootLink to={BIZ.shopUrl} external testid="foot-shop">
                                    Shop Online <ExternalLink size={11} style={{ display: "inline", marginLeft: 4, verticalAlign: "middle" }} />
                                </FootLink>
                            </li>
                            <li><FootLink to="/curbside-pickup" testid="foot-curbside">Curbside Pickup</FootLink></li>
                            <li>
                                <FootLink to={`mailto:${BIZ.email}?subject=${encodeURIComponent("Bottle Reservation Inquiry")}`} external testid="foot-reserve">
                                    Reserve a Bottle
                                </FootLink>
                            </li>
                            <li><FootLink to="/gift-cards" testid="foot-gift">Gift Cards</FootLink></li>
                            <li><FootLink to="/events-and-weddings" testid="foot-events">Weddings &amp; Events</FootLink></li>
                            <li><FootLink to="/business-accounts" testid="foot-business">Business Accounts</FootLink></li>
                            <li><FootLink to="/contact" testid="foot-contact">Contact Us</FootLink></li>
                        </ul>
                    </div>

                    {/* VISIT */}
                    <div>
                        <ColHeading>Visit</ColHeading>
                        <ul className="flex flex-col gap-3" style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 14, color: FOOTER_TEXT, opacity: 0.85, lineHeight: 1.7 }}>
                            <li className="flex items-start gap-2" style={{ justifyContent: "inherit" }}>
                                <MapPin size={14} style={{ color: FOOTER_GOLD, flexShrink: 0, marginTop: 4 }} />
                                <span>
                                    {BIZ.address.line1}<br />
                                    {BIZ.address.city}, {BIZ.address.state} {BIZ.address.zip}
                                </span>
                            </li>
                            <li style={{ color: FOOTER_MUTED, fontSize: 13, lineHeight: 1.65, marginTop: 4 }}>
                                Mon–Thu &middot; 10a–10p<br />
                                Fri–Sat &middot; 10a–11p<br />
                                Sun &middot; 10a–10p
                            </li>
                            <li className="flex items-center gap-2" style={{ marginTop: 4, justifyContent: "inherit" }}>
                                <Phone size={14} style={{ color: FOOTER_GOLD, flexShrink: 0 }} />
                                <FootLink to={`tel:${BIZ.phoneTel}`} external testid="foot-phone">{BIZ.phone}</FootLink>
                            </li>
                            <li className="flex items-center gap-2" style={{ justifyContent: "inherit" }}>
                                <Mail size={14} style={{ color: FOOTER_GOLD, flexShrink: 0 }} />
                                <FootLink to={`mailto:${BIZ.email}`} external testid="foot-email">{BIZ.email}</FootLink>
                            </li>
                            <li className="flex items-center gap-2" style={{ justifyContent: "inherit" }}>
                                <Navigation size={14} style={{ color: FOOTER_GOLD, flexShrink: 0 }} />
                                <FootLink to={directionsHref} external testid="foot-directions">Get Directions</FootLink>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* LEGAL STRIP */}
            <div style={{ borderTop: `1px solid ${FOOTER_BORDER}` }}>
                <div className="footer-legal ls-container" style={{ padding: "26px 0 30px" }}>
                    <ul className="flex flex-wrap justify-center gap-x-5 gap-y-2 mb-4" style={{ listStyle: "none", padding: 0, fontSize: 12 }} data-testid="footer-legal-row">
                        <li><FootLink to="/responsible-drinking" testid="foot-responsible">Responsible Drinking</FootLink></li>
                        <li style={{ color: FOOTER_BORDER }}>·</li>
                        <li><FootLink to="/privacy" testid="foot-privacy">Privacy Policy</FootLink></li>
                        <li style={{ color: FOOTER_BORDER }}>·</li>
                        <li><FootLink to="/terms" testid="foot-terms">Terms</FootLink></li>
                        <li style={{ color: FOOTER_BORDER }}>·</li>
                        <li><FootLink to="/accessibility" testid="foot-accessibility">Accessibility</FootLink></li>
                        <li style={{ color: FOOTER_BORDER }}>·</li>
                        <li><FootLink to="/guides/missouri-liquor-laws" testid="foot-laws">Missouri Liquor Laws</FootLink></li>
                    </ul>
                    <p style={{ textAlign: "center", color: FOOTER_MUTED, fontSize: 12, lineHeight: 1.6, maxWidth: 760, margin: "0 auto" }}>
                        © {new Date().getFullYear()} {BIZ.name} · Built with care in Clinton, Missouri 🥃<br />
                        Licensed Missouri retailer. Please drink responsibly. Must be 21+ to purchase. We ID anyone who appears under 40.
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
